import { HttpClient } from './client/httpClient';
import backendHttpClient from './client/backendHttpClient';
import { DeploymentModel, ServerReloadStatus } from '../models/deploymentModel';
import { ErrorHandler } from './error/errorHandler';
import apiErrorHandler from './error/apiErrorHandler';
import { DeploymentResponse } from './responses/deploymentResponse';

class DeploymentApi {
  constructor(private readonly httpClient: HttpClient,
              private readonly errorHandler: ErrorHandler) {}

  public fetchAll(): Promise<DeploymentModel[]> {
    return this.httpClient.get<DeploymentResponse[]>(`/deployments`)
      .then(response => response.map(it => this.mapToDeploymentModel(it)))
      .catch(error => this.errorHandler.handleError(error, 'Failed to fetch all deployments'));
  }

  public fetchDeployment(deploymentId: string): Promise<DeploymentModel> {
    return this.httpClient.get<DeploymentResponse>(`/deployments/${deploymentId}`)
      .then(response => this.mapToDeploymentModel(response))
      .catch(error => this.errorHandler.handleError(error, 'Failed to fetch deployment by id'));
  }

  public rerunDeployment(deploymentId: string): Promise<void> {
    return this.httpClient.post<void>(`/deployments/${deploymentId}/rerun`, {})
      .catch(error => this.errorHandler.handleError(error, 'Failed to rerun deployment'));
  }

  public addNewDeployment(serverId: string, commitId: string): Promise<void> {
    return this.httpClient.post<void>(`/deployments`, { serverId: serverId, commitId: commitId })
      .catch(error => this.errorHandler.handleError(error, 'Failed to add new deployment'));
  }

  private mapToDeploymentModel(response: DeploymentResponse): DeploymentModel {
    const { status: deploymentStatus } = response;

    return {
      id: response.id,
      serverId: response.serverId,
      commitId: response.commitId,
      status: {
        state: deploymentStatus.state,
        processId: deploymentStatus.processId,
        createdDate: deploymentStatus.createdDate,
        errorMessage: deploymentStatus.errorMessage,
        stackTrace: deploymentStatus.stackTrace,
      },
      serverReloads: this.mapToServerReloads(response),
      author: response.author,
      createdDate: deploymentStatus.createdDate,
      lastModifiedDate: response.lastModifiedDate,
    };
  }

  private mapToServerReloads(response: DeploymentResponse): ServerReloadStatus[] {
    return response.serverReloads.map(serverReload => ({
      reloadMethod: serverReload.reloadMethod,
      createdDate: serverReload.createdDate,
      success: serverReload.success,
      errorMessage: serverReload.errorMessage,
    }));
  }
}

export default new DeploymentApi(backendHttpClient, apiErrorHandler);
